import { useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthStore } from '../stores/authStore';
import type { Permission } from '../types/permissions';
import type { UserRole } from '../types/auth';
import { isAuditRole } from '../utils/roles';
import { 
  DEFAULT_ROLE_PERMISSIONS,
  getDefaultPermissionsForRole,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS
} from '../types/permissions';

/**
 * Hook pour gérer les permissions de l'utilisateur connecté
 * 
 * Les permissions et rôles proviennent de 3 sources (par ordre de priorité) :
 * 1. user.role et user.permissions[] de l'API /auth/me (authStore) - PRIORITÉ HAUTE
 * 2. Rôles du token JWT Auth0 (claims) - FALLBACK
 * 3. Permissions par défaut du rôle - DERNIER RECOURS
 * 
 * Utilisation :
 * ```tsx
 * const { can, canAll, canAny, isAdmin, isAuditor, role } = usePermissions();
 * 
 * // Vérifier une permission
 * if (can('create_entries')) { ... }
 * 
 * // Vérifier plusieurs permissions (toutes requises)
 * if (canAll(['create_entries', 'validate_entries'])) { ... }
 * 
 * // Vérifier au moins une permission
 * if (canAny(['create_entries', 'delete_entries'])) { ... }
 * 
 * // Vérifier le rôle
 * if (isAdmin) { ... }
 * ```
 */
export function usePermissions() {
  const { user: authStoreUser } = useAuthStore();
  const { user: auth0User } = useAuth0();
  
  // Extraire les rôles du token JWT Auth0 (fallback)
  const auth0Roles = useMemo(() => {
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
    if (auth0User && audience) {
      const roles = auth0User[`${audience}/roles`] as string[] | undefined;
      return roles || [];
    }
    return [];
  }, [auth0User]);
  
  // Rôle de l'utilisateur - Priorité: authStore > Auth0 token > défaut
  const role: UserRole = useMemo(() => {
    // 1. Priorité: rôle de l'API /auth/me (authStore)
    if (authStoreUser?.role) {
      return authStoreUser.role as UserRole;
    }
    
    // 2. Fallback: rôle du token Auth0
    if (auth0Roles.length > 0) {
      // Prendre le rôle le plus privilégié si plusieurs
      // Hiérarchie alignée avec le backend
      const roleHierarchy: UserRole[] = ['super_admin', 'admin', 'manager', 'accountant', 'analyst', 'audit', 'expert_comptable', 'viewer'];
      for (const hierarchyRole of roleHierarchy) {
        if (auth0Roles.includes(hierarchyRole)) {
          return hierarchyRole;
        }
      }
      // Si le rôle Auth0 existe mais n'est pas dans notre hiérarchie
      return auth0Roles[0] as UserRole;
    }
    
    // 3. Défaut
    return 'viewer';
  }, [authStoreUser?.role, auth0Roles]);
  
  // Permissions de l'utilisateur
  // Priorité: permissions API > permissions par défaut du rôle
  const permissions = useMemo(() => {
    // Si l'utilisateur a des permissions définies par l'API /auth/me, les utiliser
    if (authStoreUser?.permissions && authStoreUser.permissions.length > 0) {
      return authStoreUser.permissions as Permission[];
    }
    // Sinon, utiliser les permissions par défaut du rôle (déterminé ci-dessus)
    return getDefaultPermissionsForRole(role);
  }, [role, authStoreUser?.permissions]);
  
  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  const can = useCallback((permission: Permission): boolean => {
    return permissions.includes(permission);
  }, [permissions]);
  
  /**
   * Vérifie si l'utilisateur a TOUTES les permissions spécifiées
   */
  const canAll = useCallback((perms: Permission[]): boolean => {
    return perms.every(p => can(p));
  }, [can]);
  
  /**
   * Vérifie si l'utilisateur a AU MOINS UNE des permissions spécifiées
   */
  const canAny = useCallback((perms: Permission[]): boolean => {
    return perms.some(p => can(p));
  }, [can]);
  
  /**
   * Vérifie si l'utilisateur ne peut PAS faire une action
   */
  const cannot = useCallback((permission: Permission): boolean => {
    return !can(permission);
  }, [can]);
  
  // Raccourcis pour les rôles courants (alignés avec le backend)
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isAudit = isAuditRole(role);
  const isAccountant = role === 'accountant';
  const isManager = role === 'manager';
  const isAnalyst = role === 'analyst';
  const isViewer = role === 'viewer';
  
  // Vérifications de niveau d'accès basées sur le rôle
  const hasAdminAccess = isAdmin || isSuperAdmin;
  const hasManagementAccess = hasAdminAccess || isManager;
  const hasAccountingAccess = hasAdminAccess || isAccountant;
  
  // Label et description du rôle
  const roleLabel = ROLE_LABELS[role];
  const roleDescription = ROLE_DESCRIPTIONS[role];
  
  return {
    // Données utilisateur
    user: authStoreUser,
    role,
    roleLabel,
    roleDescription,
    permissions,
    
    // Méthodes de vérification
    can,
    canAll,
    canAny,
    cannot,
    
    // Raccourcis de rôle (noms alignés avec le backend)
    isSuperAdmin,
    isAdmin,
    isAudit,        // Anciennement isAuditor
    isAccountant,   // Anciennement isComptable
    isManager,      // Anciennement isGerant
    isAnalyst,
    isViewer,       // Anciennement isUser
    
    // Alias pour compatibilité (deprecated)
    /** @deprecated Utiliser isAudit */
    isAuditor: isAudit,
    /** @deprecated Utiliser isAccountant */
    isComptable: isAccountant,
    /** @deprecated Utiliser isManager */
    isGerant: isManager,
    /** @deprecated Utiliser isViewer */
    isUser: isViewer,
    
    // Niveaux d'accès
    hasAdminAccess,
    hasManagementAccess,
    hasAccountingAccess,
    
    // Sources de données (pour debugging)
    roleSource: authStoreUser?.role ? 'api' : (auth0Roles.length > 0 ? 'auth0' : 'default'),
    auth0Roles,
  };
}

/**
 * Type utilitaire pour les props de composants avec contrôle d'accès
 */
export interface WithPermissionProps {
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean; // Si true, toutes les permissions sont requises
  fallback?: React.ReactNode;
}

/**
 * Composant wrapper pour le contrôle d'accès conditionnel
 * 
 * Usage:
 * ```tsx
 * <PermissionGate permission="journals:create">
 *   <Button>Créer une écriture</Button>
 * </PermissionGate>
 * 
 * <PermissionGate permissions={['journals:edit', 'journals:delete']} requireAll>
 *   <AdminPanel />
 * </PermissionGate>
 * ```
 */
export function PermissionGate({ 
  children, 
  permission, 
  permissions, 
  requireAll = false,
  fallback = null 
}: WithPermissionProps & { children: React.ReactNode }) {
  const { can, canAll, canAny } = usePermissions();
  
  let hasAccess = false;
  
  if (permission) {
    hasAccess = can(permission);
  } else if (permissions) {
    hasAccess = requireAll ? canAll(permissions) : canAny(permissions);
  } else {
    // Pas de permission requise = toujours accessible
    hasAccess = true;
  }
  
  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * HOC pour protéger un composant entier
 * 
 * Usage:
 * ```tsx
 * const ProtectedSettings = withPermission(SettingsPage, 'settings:edit');
 * ```
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: Permission,
  FallbackComponent?: React.ComponentType
) {
  return function ProtectedComponent(props: P) {
    const { can } = usePermissions();
    
    if (!can(permission)) {
      return FallbackComponent ? <FallbackComponent /> : null;
    }
    
    return <Component {...props} />;
  };
}

/**
 * Hook pour obtenir les permissions d'un rôle spécifique (pour l'admin)
 */
export function useRolePermissions(role: UserRole) {
  return useMemo(() => ({
    permissions: DEFAULT_ROLE_PERMISSIONS[role] || [],
    label: ROLE_LABELS[role],
    description: ROLE_DESCRIPTIONS[role],
  }), [role]);
}
